import React from 'react'
import { cn } from '../../utils/cn'

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string
}

/** Padding kommt von .card (Token --ui-card-pad); eigene p-*-Klassen der Seite gewinnen (Utilities-Layer). Mit CardHeader/CardContent entfällt es. */

export function Card({ children, className, ...props }: CardProps) {
  const strukturiert = React.Children.toArray(children).some(
    c => React.isValidElement(c) && (c.type === CardHeader || c.type === CardContent)
  )
  return (
    <div className={cn('card', strukturiert && 'card-bare', className)} {...props}>
      {children}
    </div>
  )
}

interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string
}

export function CardHeader({ children, className, ...props }: CardHeaderProps) {
  return (
    <div className={cn('tile-head !min-h-0 !py-2.5', className)} {...props}>
      {children}
    </div>
  )
}

interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string
}

export function CardContent({ children, className, ...props }: CardContentProps) {
  return (
    <div className={cn('tile-body', className)} {...props}>
      {children}
    </div>
  )
}

interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  className?: string
}

export function CardTitle({ children, className, ...props }: CardTitleProps) {
  return (
    <h3 className={cn('text-base font-semibold text-dark-text', className)} {...props}>
      {children}
    </h3>
  )
}

interface CardDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  className?: string
}

export function CardDescription({ children, className, ...props }: CardDescriptionProps) {
  return (
    <p className={cn('text-sm text-dark-text-muted mt-1', className)} {...props}>
      {children}
    </p>
  )
}

interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'secondary'
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export function Badge({ variant = 'default', size = 'md', className, children, ...props }: BadgeProps) {
  const variantStyles = {
    default: 'bg-dark-bg-tertiary text-dark-text-muted',
    success: 'bg-status-ok/15 text-status-ok',
    warning: 'bg-status-warn/15 text-status-warn',
    danger: 'bg-status-danger/15 text-status-danger',
    info: 'bg-status-info/15 text-status-info',
    secondary: 'bg-dark-bg-tertiary text-dark-text'
  }

  const sizeStyles = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base'
  }

  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-full',
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
      {...props}
    >
      {children}
    </span>
  )
}